import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  PracticeProfileStep,
  PracticeStepNameEnum,
  ProviderProfileStep,
  ProviderStepNameEnum,
  StepStatusEnum,
  ViewModel,
} from "./schema"
import { Provider } from "../Provider/schema"
import { providerUpdated } from "../Provider/model"
import {
  getAvailabilityStepStatus,
  getClientFocusStepStatus,
  getDisclosureStepStatus,
  getInsuranceStepStatus,
  getLiabilityInsuranceStepStatus,
  getLocationsStepStatus,
  getPersonalInfoStepStatus,
  getQualificationsStepStatus,
  getServiceStepStatus,
} from "./helpers/providerHelpers"
import { updatePracticeCompleteness } from "../Practice/model"
import { PracticeCompleteness } from "../Practice/schema"
import {
  updateCompletedOnboardingSteps,
  updateUser,
  UserOnboardingStepsEnum,
} from "../Account/userSlice"

const practiceProfileSteps: PracticeProfileStep[] = [
  {
    name: PracticeStepNameEnum.PRACTICE_INFORMATION,
    status: StepStatusEnum.READY,
  },
  { name: PracticeStepNameEnum.TAX, status: StepStatusEnum.READY },
  {
    name: PracticeStepNameEnum.AUTH_AND_RELEASES,
    status: StepStatusEnum.READY,
  },
  {
    name: PracticeStepNameEnum.LIABILITY,
    status: StepStatusEnum.READY,
    hide: true,
  },
  { name: PracticeStepNameEnum.OFFICES, status: StepStatusEnum.READY },
  {
    name: PracticeStepNameEnum.PRACTICE_AGREEMENT,
    status: StepStatusEnum.READY,
    hide: true,
  },
]
const providerProfileSteps: ProviderProfileStep[] = [
  {
    name: ProviderStepNameEnum.PERSONAL_INFORMATION,
    status: StepStatusEnum.READY,
  },
  { name: ProviderStepNameEnum.CLIENT_FOCUS, status: StepStatusEnum.READY },
  { name: ProviderStepNameEnum.AVAILABILITY, status: StepStatusEnum.READY },
  { name: ProviderStepNameEnum.QUALIFICATIONS, status: StepStatusEnum.READY },
  { name: ProviderStepNameEnum.INSURANCE, status: StepStatusEnum.READY },
  { name: ProviderStepNameEnum.LOCATIONS, status: StepStatusEnum.READY },
  {
    name: ProviderStepNameEnum.LIABILITY_INSURANCE,
    status: StepStatusEnum.READY,
  },
  { name: ProviderStepNameEnum.SERVICES, status: StepStatusEnum.READY },
  { name: ProviderStepNameEnum.DISCLOSURE, status: StepStatusEnum.READY },
]

export const initialViewModel: ViewModel = {
  practiceProfileSteps: practiceProfileSteps,
  providerProfileSteps: providerProfileSteps,
}

export interface ViewModelState {
  viewModel: ViewModel
}

const initialState: ViewModelState = {
  viewModel: initialViewModel,
}

export const viewModelSlice = createSlice({
  name: "viewModel",
  initialState,
  reducers: {
    updateViewModel: (
      state: { viewModel: ViewModel },
      { payload }: PayloadAction<Partial<ViewModel>>,
    ) => {
      state.viewModel = {
        ...state.viewModel,
        ...payload,
        // conditionally spread nested objects and full replace lists here
        practiceProfileSteps: payload.practiceProfileSteps
          ? payload.practiceProfileSteps
          : state.viewModel.practiceProfileSteps,
        providerProfileSteps: payload.providerProfileSteps
          ? payload.providerProfileSteps
          : state.viewModel.providerProfileSteps,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        updatePracticeCompleteness,
        (state, action: PayloadAction<PracticeCompleteness>) => {
          const practiceCompleteness = action.payload

          // Get existing tax information step to preserve its status
          const existingTaxInfoStep = state.viewModel.practiceProfileSteps.find(
            (step) => step.name === PracticeStepNameEnum.TAX,
          )
          // Get existing offices step (if any) to preserve its status
          const existingOfficesStep = state.viewModel.practiceProfileSteps.find(
            (step) => step.name === PracticeStepNameEnum.OFFICES,
          )
          // Get existing agreement step (if any) to preserve its status
          const existingAgreementStep =
            state.viewModel.practiceProfileSteps.find(
              (step) => step.name === PracticeStepNameEnum.PRACTICE_AGREEMENT,
            )

          // update the practicerProfileSteps
          const updatedPracticeProfileSteps: PracticeProfileStep[] = [
            {
              name: PracticeStepNameEnum.PRACTICE_INFORMATION,
              status: practiceCompleteness.isPracticeInformationComplete
                ? StepStatusEnum.COMPLETE
                : StepStatusEnum.READY,
            },
            {
              name: PracticeStepNameEnum.AUTH_AND_RELEASES,
              status: practiceCompleteness.isPracticeAuthReleaseComplete
                ? StepStatusEnum.COMPLETE
                : StepStatusEnum.READY,
            },
            {
              name: PracticeStepNameEnum.LIABILITY,
              status:
                practiceCompleteness.isPracticeLiabilityInformationComplete
                  ? StepStatusEnum.COMPLETE
                  : StepStatusEnum.READY,
              hide: !practiceCompleteness.isPracticeUsingLiabilityInsurance,
            },
          ]

          // Add or preserve the tax information step
          if (existingTaxInfoStep) {
            updatedPracticeProfileSteps.push(existingTaxInfoStep)
          }
          // Add or preserve the offices step
          if (existingOfficesStep) {
            updatedPracticeProfileSteps.push(existingOfficesStep)
          }
          // Add or preserve the agreement step
          if (existingAgreementStep) {
            updatedPracticeProfileSteps.push(existingAgreementStep)
          }

          // update "practiceAgreement" hide field based off if all other steps are complete
          const practiceAgreementReady = updatedPracticeProfileSteps
            .filter(
              (step) => step.name !== PracticeStepNameEnum.PRACTICE_AGREEMENT,
            )
            .filter((step) => !step.hide)
            .every((step) => step.status === StepStatusEnum.COMPLETE)
          if (practiceAgreementReady) {
            const practiceAgreementIndex =
              updatedPracticeProfileSteps.findIndex(
                (step) => step.name === PracticeStepNameEnum.PRACTICE_AGREEMENT,
              )
            updatedPracticeProfileSteps[practiceAgreementIndex].hide = false
          }
          state.viewModel.practiceProfileSteps = updatedPracticeProfileSteps
        },
      )
      .addCase(providerUpdated, (state, action: PayloadAction<Provider>) => {
        const provider = action.payload

        // update the providerProfileSteps
        const updatedProviderProfileSteps: ProviderProfileStep[] = [
          {
            name: ProviderStepNameEnum.PERSONAL_INFORMATION,
            status: getPersonalInfoStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.CLIENT_FOCUS,
            status: getClientFocusStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.AVAILABILITY,
            status: getAvailabilityStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.QUALIFICATIONS,
            status: getQualificationsStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.INSURANCE,
            status: getInsuranceStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.SERVICES,
            status: getServiceStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.LOCATIONS,
            status: getLocationsStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.LIABILITY_INSURANCE,
            status: getLiabilityInsuranceStepStatus(provider),
          },
          {
            name: ProviderStepNameEnum.DISCLOSURE,
            status: getDisclosureStepStatus(provider),
          },
        ]
        state.viewModel.providerProfileSteps = updatedProviderProfileSteps
      })
      .addCase(
        updateCompletedOnboardingSteps,
        (state, action: PayloadAction<UserOnboardingStepsEnum[]>) => {
          // Update the tax info step based on the updated user onboarding steps
          const isTaxInfoComplete = action.payload.includes(
            UserOnboardingStepsEnum.TAX_INFORMATION,
          )
          const taxInfoStepIndex =
            state.viewModel.practiceProfileSteps.findIndex(
              (step) => step.name === PracticeStepNameEnum.TAX,
            )

          // Update the office setup step based on the updated user onboarding steps
          const isOfficeSetupComplete = action.payload.includes(
            UserOnboardingStepsEnum.OFFICE_SETUP,
          )
          const officesStepIndex =
            state.viewModel.practiceProfileSteps.findIndex(
              (step) => step.name === PracticeStepNameEnum.OFFICES,
            )

          // Update the agreement step based on the updated user onboarding steps
          const isAgreementComplete = action.payload.includes(
            UserOnboardingStepsEnum.PRACTICE_SETUP,
          )
          const agreementStepIndex =
            state.viewModel.practiceProfileSteps.findIndex(
              (step) => step.name === PracticeStepNameEnum.PRACTICE_AGREEMENT,
            )

          if (taxInfoStepIndex !== -1) {
            // Update existing TAX step
            state.viewModel.practiceProfileSteps[taxInfoStepIndex] = {
              ...state.viewModel.practiceProfileSteps[taxInfoStepIndex],
              status: isTaxInfoComplete
                ? StepStatusEnum.COMPLETE
                : StepStatusEnum.READY,
            }
          }
          if (officesStepIndex !== -1) {
            // Update existing OFFICES step
            state.viewModel.practiceProfileSteps[officesStepIndex] = {
              ...state.viewModel.practiceProfileSteps[officesStepIndex],
              status: isOfficeSetupComplete
                ? StepStatusEnum.COMPLETE
                : StepStatusEnum.READY,
            }
          }
          if (agreementStepIndex !== -1) {
            // Update existing PRACTICE_AGREEMENT step
            state.viewModel.practiceProfileSteps[agreementStepIndex] = {
              ...state.viewModel.practiceProfileSteps[agreementStepIndex],
              status: isAgreementComplete
                ? StepStatusEnum.COMPLETE
                : StepStatusEnum.READY,
            }
          }
        },
      )
      .addCase(updateUser, (state, action) => {
        // Update the tax info step based on the updated user onboarding steps
        const isTaxInfoComplete =
          action.payload.completedUserOnboardingSteps.includes(
            UserOnboardingStepsEnum.TAX_INFORMATION,
          )
        const taxInfoStepIndex = state.viewModel.practiceProfileSteps.findIndex(
          (step) => step.name === PracticeStepNameEnum.TAX,
        )

        // Update the office setup step based on the completed user onboarding steps
        const isOfficeSetupComplete =
          action.payload.completedUserOnboardingSteps.includes(
            UserOnboardingStepsEnum.OFFICE_SETUP,
          )
        const officesStepIndex = state.viewModel.practiceProfileSteps.findIndex(
          (step) => step.name === PracticeStepNameEnum.OFFICES,
        )

        // Update the agreement step based on the completed user onboarding steps
        const isAgreementComplete =
          action.payload.completedUserOnboardingSteps.includes(
            UserOnboardingStepsEnum.PRACTICE_SETUP,
          )
        const agreementStepIndex =
          state.viewModel.practiceProfileSteps.findIndex(
            (step) => step.name === PracticeStepNameEnum.PRACTICE_AGREEMENT,
          )

        if (taxInfoStepIndex !== -1) {
          state.viewModel.practiceProfileSteps[taxInfoStepIndex] = {
            ...state.viewModel.practiceProfileSteps[taxInfoStepIndex],
            status: isTaxInfoComplete
              ? StepStatusEnum.COMPLETE
              : StepStatusEnum.READY,
          }
        }
        if (officesStepIndex !== -1) {
          state.viewModel.practiceProfileSteps[officesStepIndex] = {
            ...state.viewModel.practiceProfileSteps[officesStepIndex],
            status: isOfficeSetupComplete
              ? StepStatusEnum.COMPLETE
              : StepStatusEnum.READY,
          }
        }
        if (agreementStepIndex !== -1) {
          state.viewModel.practiceProfileSteps[agreementStepIndex] = {
            ...state.viewModel.practiceProfileSteps[agreementStepIndex],
            status: isAgreementComplete
              ? StepStatusEnum.COMPLETE
              : StepStatusEnum.READY,
          }
        }
      })
  },
})

// Export actions and reducer
export const { updateViewModel } = viewModelSlice.actions

export const selectPracticeProfileSteps = (state: {
  viewModel: ViewModelState
}): PracticeProfileStep[] => {
  return state.viewModel.viewModel.practiceProfileSteps
}

export const selectProviderProfileSteps =
  (isNoVirtualLocationEnabled?: boolean) =>
  (state: { viewModel: ViewModelState }): ProviderProfileStep[] => {
    const steps = state.viewModel.viewModel.providerProfileSteps
    if (isNoVirtualLocationEnabled !== false) return steps
    return steps.map((step) =>
      step.name === ProviderStepNameEnum.SERVICES
        ? { ...step, status: StepStatusEnum.COMPLETE }
        : step,
    )
  }

export const selectValidPracticeProfileSteps = createSelector(
  selectPracticeProfileSteps,
  (practiceProfileSteps) => {
    // show only non-hidden steps
    const validPracticeProfileSteps = practiceProfileSteps.filter(
      (step) => !step.hide,
    )
    return validPracticeProfileSteps
  },
)

export const selectPracticeAgreementReady = createSelector(
  selectPracticeProfileSteps,
  (practiceProfileSteps) => {
    const practiceAgreementIndex = practiceProfileSteps.findIndex(
      (step) => step.name === PracticeStepNameEnum.PRACTICE_AGREEMENT,
    )
    const practiceAgreementReady =
      practiceProfileSteps[practiceAgreementIndex].hide === false
    return practiceAgreementReady
  },
)

export const selectPracticeStep = (stepName: PracticeStepNameEnum) =>
  createSelector(
    selectPracticeProfileSteps,
    (steps): PracticeProfileStep | undefined =>
      steps.find((step) => step.name === stepName),
  )

export default viewModelSlice.reducer
